import React, { useRef } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  IconButton,
  Stack,
  Chip
} from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';
import { TransportationOption } from '@/types';
import EditTransportationOptionForm from '@/components/transportation/EditTransportationOptionForm';
import { useI18n } from '@/hooks/useI18n';

interface TransportationOptionCardProps {
  transportationOption: TransportationOption;
  onUpdateTransportationOption: (option: TransportationOption) => void;
  onDeleteTransportationOption: (optionId: string) => void;
}

const TransportationOptionCard: React.FC<TransportationOptionCardProps> = ({
  transportationOption,
  onUpdateTransportationOption,
  onDeleteTransportationOption
}) => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const { t, formatCurrency } = useI18n();

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'individual':
        return 'primary';
      case 'group':
        return 'secondary';
      case 'public':
        return 'success';
      default:
        return 'default';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'individual':
        return '🚗';
      case 'group':
        return '🚌';
      case 'public':
        return '🚆';
      default:
        return '🚗';
    }
  };

  return (
    <>
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flexGrow: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Stack spacing={1}>
              <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold' }}>
                {getTypeIcon(transportationOption.type)} {transportationOption.name}
              </Typography>
              <Chip 
                label={t(`transportation.types.${transportationOption.type}`)} 
                color={getTypeColor(transportationOption.type) as any}
                size="small"
              />
            </Stack>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <IconButton
                size="small"
                onClick={() => triggerRef.current?.click()}
                aria-label={t('common.edit')}
              >
                <Edit fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => onDeleteTransportationOption(transportationOption.id)}
                aria-label={t('common.delete')}
                color="error"
              >
                <Delete fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              💰 {t('common.price')}
            </Typography>
            <Typography variant="h6" color={transportationOption.pricePerPerson < 0 ? 'success.main' : 'primary'} sx={{ fontWeight: 'bold' }}>
              {transportationOption.pricePerPerson === 0 
                ? t('transportation.free')
                : transportationOption.pricePerPerson < 0
                  ? `${t('transportation.discount')} ${formatCurrency(Math.abs(transportationOption.pricePerPerson))}`
                  : formatCurrency(transportationOption.pricePerPerson)
              }
              {transportationOption.pricePerPerson !== 0 && (
                <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                  / {t('common.person')}
                </Typography>
              )}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      <EditTransportationOptionForm
        transportationOption={transportationOption}
        onUpdateTransportationOption={onUpdateTransportationOption}
        trigger={
          <Button 
            ref={triggerRef}
            style={{ display: 'none' }}
          >
            {t('transportation.form.hidden')}
          </Button>
        }
      />
    </>
  );
};

export default TransportationOptionCard;